<script setup lang="ts">
import { useNotification } from '@kyvg/vue3-notification'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { pikimoni } from '@/helpers/pikimoni'

interface StudentContext {
  id: number
  name: string
  last_name: string
  patronymic: string
  has_pikimoni_binding: boolean
}

interface CoursePart {
  id: number
  title: string
  image: string
  view_url: string
}

interface CourseItem {
  id: number
  title: string
  desc: string
  parts: CoursePart[]
}

interface MethodItem {
  id: number
  title: string
  courses: CourseItem[]
}

interface LanguageItem {
  id: number
  title: string
  methods: MethodItem[]
}

interface PageContext {
  student: StudentContext
  languages: LanguageItem[]
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()
const { notify } = useNotification()

function goBack() {
  window.history.back()
}

async function loginToPikimoni() {
  const win = window.open('about:blank', '_blank')
  if (!win) {
    notify({ type: 'error', title: 'Разрешите всплывающие окна и повторите попытку' })
    return
  }

  try {
    const url = await pikimoni.getLoginUrl('student', ctx.student.id)
    win.location.href = url
  } catch (err) {
    console.error(err)
    win.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof err === 'string') title = err
    if (err instanceof Error && err.message) title = err.message

    notify({ type: 'error', title })
  }
}
</script>

<template>
  <div class="choose-course-page">
    <aside class="aside">
      <v-icon v-if="isMobile" width="118" height="40">logo</v-icon>
      <v-icon v-else width="172" height="64">logo</v-icon>

      <div class="aside__user user mt-4 mt-d-3">
        <v-icon v-if="!isMobile" class="user__avatar" size="48">mdi-account</v-icon>
        {{ ctx.student.last_name }} {{ ctx.student.name }} {{ ctx.student.patronymic }}
      </div>

      <v-button v-if="ctx.student.has_pikimoni_binding" mode="primary block" class="my-2" @click="loginToPikimoni">
        Войти в Pikimoni
      </v-button>
    </aside>

    <div class="content">
      <header class="header">
        <h1 class="header__title">Курсы</h1>
      </header>

      <div class="courses">
        <v-button mode="flat primary large block" style="max-width: 560px" @click="goBack">
          <v-icon class="mr-1">mdi-arrow-left</v-icon>
          Вернуться назад
        </v-button>

        <template v-for="lang in ctx.languages" :key="lang.id">
          <div class="language__title mt-3 mb-2">{{ lang.title }}</div>

          <div v-for="method in lang.methods" :key="method.id" class="language-section">
            <div class="language-section__title text-subheading mb-2">{{ method.title }}</div>

            <div class="language-section__courses">
              <template v-for="course in method.courses" :key="course.id">
                <div v-for="part in course.parts" :key="part.id" class="course-card mb-2" :data-id="part.id">
                  <div class="course-card__image">
                    <v-img v-if="part.image" :src="part.image" :width="184" :height="184" />
                  </div>

                  <div class="course-card__content mt-1 mt-d-0">
                    <div class="course-card__header d-flex mb-1">
                      <h3 class="course-card__title">{{ course.title }}</h3>
                      <div class="course-card__part ml-2">{{ part.title }}</div>
                    </div>

                    <div class="course-card__description mb-2 mb-d-3">
                      {{ course.desc }}
                    </div>

                    <v-button :mode="isMobile ? 'primary large block' : 'primary'" :href="part.view_url">
                      Открыть
                    </v-button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/student/choose_course.scss' as *;
</style>
